<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">Build Check Tool</h1>
<br/>
@if (permission()) {
    <table class="files-input" [class.hidden]="!showFileInput">
        <tr>
            <td>
                <app-file-upload-card (filesSelected)="onFilesSelected($event, 'harnessMatrixFiles')"
                                      [header]="'Harness Matrix:'" [multiple]="true"
                                      [subheader]="'*Select 1 or more Harness Matrix files'"></app-file-upload-card>
            </td>
            <td>
                <app-file-upload-card (filesSelected)="onFileSelected($event, 'vehicleBuildFile')"
                                      [header]="'Vehicle Build:'"
                                      [subheader]="'*Select 1 Vehicle Build file'"></app-file-upload-card>
            </td>
            <td>
                <app-file-upload-card (filesSelected)="onFileSelected($event, 'kitMatrixFile')"
                                      [header]="'Kit Matrix:'"
                                      [subheader]="'Select 1 Kit Matrix to run report'"></app-file-upload-card>
            </td>
        </tr>
        <tr>
            <td></td>
            <td></td>
            <td>
                <div class="buttons">
                    @if (adminPermission()) {
                        <div class="flex items-center gap-2">
                            <p-checkbox [(ngModel)]="bypassValidation" inputId="bypassValidation" [binary]="true"/>
                            <label for="bypassValidation">Bypass Validation &nbsp;</label>
                        </div>
                    }
                    <p-button label="Run Report" icon="pi pi-check" class="gm-btn-primary mr-2"
                              [disabled]="!(harnessMatrixFiles?.length && vehicleBuildFile
              && kitMatrixFile) || !permission()" (click)="runReport()">
                    </p-button>
                    <p-button label="Generate Kit Matrix" icon="pi pi-check" class="gm-btn-primary mr-2"
                              [disabled]="! (harnessMatrixFiles?.length && vehicleBuildFile
              && !kitMatrixFile) || !permission()" (click)="processHarnesses()">
                    </p-button>
                    @if (adminPermission()) {
                        <span [pTooltip]="bctDetailsDisabledReason()" tooltipPosition="top" style="display: inline-block; vertical-align: middle">
                            <p-button label="BCT Details" icon="pi pi-check" class="gm-btn-primary mr-2"
                                      [disabled]="!!bctDetailsDisabledReason()"
                                      (click)="generateBctDetails()">
                            </p-button>
                        </span>
                    }
                </div>
            </td>
        </tr>
    </table>

@if (progress() > 0) {
  <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
}
@if (!showFileInput && showOptionsComparison && permission()) {
<div id="buildCheckToolTables">
  <p-select [options]="harnesses" [(ngModel)]="selectedHarness" [showClear]="true" placeholder="Select Harness"
    [filter]="true" filterBy="harnessName" class="mr-2 mb-2">
    <ng-template pTemplate="selectedItem">
      @if (selectedHarness) {
        <div class="flex items-center gap-2">
          <div>{{selectedHarness.harnessName}} - ({{selectedHarness.vehicleBuildOptionsList.length}} builds)</div>
        </div>
      }
    </ng-template>
    <ng-template let-harness pTemplate="item">
      <div class="flex items-center gap-2">
        <div>{{harness.harnessName}} - ({{harness.vehicleBuildOptionsList.length}} builds)</div>
      </div>
    </ng-template>
  </p-select>
  @if (selectedHarness != undefined) {
    <p-select [options]="selectedHarness.vehicleBuildOptionsList"
      [(ngModel)]="selectedVehicleBuildOptions[0]" [showClear]="true" class="mr-2 mb-2"
      placeholder="Select Vehicle Build" [filter]="true" filterBy="buildIdPartNumber">
      <ng-template pTemplate="selectedItem">
        @if (selectedVehicleBuildOptions[0]) {
          <div class="flex items-center gap-2">
            <div>{{formatVehicleBuildText(selectedVehicleBuildOptions[0])}}</div>
          </div>
        }
      </ng-template>
      <ng-template let-vehicleBuild pTemplate="item">
        <div class="flex items-center gap-2">
          <div>{{formatVehicleBuildText(vehicleBuild)}}</div>
        </div>
      </ng-template>
    </p-select>
  }
  <div class="optionsTable">
    @if (selectedHarness && selectedVehicleBuildOptions.length > 0 && selectedVehicleBuildOptions[0]) {
      <p-table class="p-datatable-gridlines" [value]="selectedVehicleBuildOptions" [tableStyle]="{ 'min-width': '50rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th pTooltip="Present in the vehicle, on in the harness matrix" tooltipPosition="top">Common Options</th>
            <th pTooltip="Present in the vehicle, present but off in the harness matrix" tooltipPosition="top">Vehicle Only (Electrical RPOs)</th>
            <th pTooltip="Present in the vehicle, not present in the harness matrix" tooltipPosition="top">Vehicle Only (Non-Electrical RPOs)</th>
            <th pTooltip="Not present in the vehicle, on in the harness matrix" tooltipPosition="top">Harness Only</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-option>
          <tr>
            <td>{{selectedVehicleBuildOptions[0].commonOptions.join("   ")}}</td>
            <td>{{selectedVehicleBuildOptions[0].vehicleOnlyElectricalOptions.join("   ")}}</td>
            <td>{{selectedVehicleBuildOptions[0].vehicleOnlyNonElectricalOptions.join("   ")}}</td>
            <td>{{selectedVehicleBuildOptions[0].harnessOnlyOptions.join("   ")}}</td>
          </tr>
        </ng-template>
      </p-table>
    }
      <div class="buttons">
        @if (selectedHarness && selectedVehicleBuildOptions.length > 0 && selectedVehicleBuildOptions[0]) {
          <p-splitbutton icon="pi pi-download" [model]="downloadButtonOptions" (onClick)="exportReport(true)"
          label="Download Single Report" class="mr-2"></p-splitbutton>
        }
        <p-button label="Change Files" (click)="messageService.clear(); showFileInput = true; showOptionsComparison = false;"
        severity="warn" class="mr-2"></p-button>
      </div>
  </div>
 </div>
}
@if (!showFileInput && showHarnessBuckets && permission()) {
<div cdkDropListGroup>
  <table>
    <tr>
      <p-select [options]="programs" [(ngModel)]="selectedProgram" [showClear]="true" class="mr-2 mb-2"
        placeholder="Select Program" [filter]="true" filterBy="programName" optionLabel="programName">
      </p-select>
    </tr>
    <tr>
      <p-select [options]="modelYears" [(ngModel)]="selectedModelYear" [showClear]="true" class="mr-2 mb-2"
        placeholder="Select Model Year" [filter]="true" filterBy="year" optionLabel="year">
      </p-select>
    </tr>
    <tr>
      <div class="buttons">
        <p-button label="Generate Kit Matrix" icon="pi pi-check" class="gm-btn-primary mr-2"
                  [disabled]="!(harnessMatrixFileBlob && vehicleBuildFile) ||
            !(selectedModelYear && selectedProgram)" (click)="generateKitMatrix()">
        </p-button>
        <p-button label="Change Files" (click)="messageService.clear(); showFileInput = true; showHarnessBuckets = false;"
        severity="warn" class="mr-2"></p-button>
      </div>
    </tr>

    <tr>
      <td>
        <div class="harness-bucket">
          <h2>VAA Kits</h2>
          <div cdkDropList [cdkDropListData]="vaaKits" class="harness-bucket-list" (cdkDropListDropped)="drop($event)">
            @for (item of vaaKits; track item) {
              <div class="harness-bucket-box" cdkDrag>{{item.harnessName}}</div>
            }
          </div>
        </div>
      </td>
      <td><br/>
        @if (vaaKits.length > 0) {
          <p-button icon="pi pi-angle-double-right" (click)="moveHarnesses(vaaKits, harnessKits);" class="gm-btn-primary"></p-button>
        }
        @if (vaaKits.length > 0 && harnessKits.length > 0) {
          <span><br/><br/></span>
        }
        @if (harnessKits.length > 0) {
          <p-button icon="pi pi-angle-double-left" (click)="moveHarnesses(harnessKits, vaaKits);" class="gm-btn-primary"></p-button>
        }
      </td>
      <td>
        <div class="harness-bucket">
          <h2>Harness Kits</h2>
          <div
            cdkDropList
            [cdkDropListData]="harnessKits"
            class="harness-bucket-list"
            (cdkDropListDropped)="drop($event)">
            @for (item of harnessKits; track item) {
              <div class="harness-bucket-box" cdkDrag>{{item.harnessName}}</div>
            }
          </div>
        </div>
      </td>
      <td><br/>
        @if (harnessKits.length > 0) {
          <p-button icon="pi pi-angle-double-right" (click)="moveHarnesses(harnessKits, poaKits);" class="gm-btn-primary"></p-button>
        }
        @if (harnessKits.length > 0 && poaKits.length > 0) {
          <span><br/><br/></span>
        }
        @if (poaKits.length > 0) {
          <p-button icon="pi pi-angle-double-left" (click)="moveHarnesses(poaKits, harnessKits);" class="gm-btn-primary"></p-button>
        }
      </td>
      <td>
        <div class="harness-bucket">
          <h2>POA (Ordered by Tier)</h2>
          <div cdkDropList [cdkDropListData]="poaKits" class="harness-bucket-list" (cdkDropListDropped)="drop($event)">
            @for (item of poaKits; track item) {
              <div class="harness-bucket-box" cdkDrag>{{item.harnessName}}</div>
            }
          </div>
        </div>
      </td>
    </tr>
  </table>
</div>
}
}





